import React, { useState, useEffect, useCallback } from 'react';
import { 
  LogOut, 
  FileText, 
  Mail, 
  Loader2, 
  CheckCircle, 
  Clock, 
  XCircle,
  Trash2,
  Calendar,
  User,
  Phone as PhoneIcon,
  Mail as MailIcon
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { 
  adminLogout, 
  getAdminQuotes, 
  getAdminContacts,
  updateQuoteStatus,
  deleteQuote,
  deleteContact
} from '../api';
import { useNavigate } from 'react-router-dom';

const AdminDashboard = () => {
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    adminLogout();
    toast.success('Déconnexion réussie');
    navigate('/admin/login');
  }, [navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [quotesData, contactsData] = await Promise.all([
        getAdminQuotes(statusFilter === 'all' ? null : statusFilter),
        getAdminContacts()
      ]);
      setQuotes(quotesData.quotes);
      setContacts(contactsData.contacts);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, handleLogout]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      await updateQuoteStatus(quoteId, newStatus);
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce devis ?')) {
      try {
        await deleteQuote(quoteId);
        toast.success('Devis supprimé');
        fetchData();
      } catch (error) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
      try {
        await deleteContact(contactId);
        toast.success('Message supprimé');
        fetchData();
      } catch (error) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', color: 'bg-yellow-500', icon: Clock },
      confirmed: { label: 'Confirmé', color: 'bg-green-500', icon: CheckCircle },
      completed: { label: 'Terminé', color: 'bg-blue-500', icon: CheckCircle },
      cancelled: { label: 'Annulé', color: 'bg-red-500', icon: XCircle }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} text-white`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-pink-50 to-fuchsia-50">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-dm.png" alt="DM" className="w-12 h-12" />
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent">
                Dashboard Admin
              </h1>
              <p className="text-sm text-gray-600">Décors Multifestif</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-red-500 text-red-500 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <Loader2 className="w-12 h-12 animate-spin text-pink-500" />
          </div>
        ) : (
          <Tabs defaultValue="quotes" className="space-y-6">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="quotes" className="text-lg">
                <FileText className="w-4 h-4 mr-2" />
                Devis ({quotes.length})
              </TabsTrigger>
              <TabsTrigger value="contacts" className="text-lg">
                <Mail className="w-4 h-4 mr-2" />
                Messages ({contacts.length})
              </TabsTrigger>
            </TabsList>

            {/* Quotes Tab */}
            <TabsContent value="quotes" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Demandes de Devis</CardTitle>
                      <CardDescription>Gérez les demandes de devis des clients</CardDescription>
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Filtrer par statut" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="pending">En attente</SelectItem>
                        <SelectItem value="confirmed">Confirmé</SelectItem>
                        <SelectItem value="completed">Terminé</SelectItem>
                        <SelectItem value="cancelled">Annulé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <CardContent>
                  {quotes.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">Aucun devis pour l'instant</p>
                  ) : (
                    <div className="space-y-4">
                      {quotes.map((quote) => (
                        <Card key={quote.id} className="shadow-md">
                          <CardContent className="pt-6">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h3 className="text-xl font-bold text-gray-900">{quote.name}</h3>
                                  {getStatusBadge(quote.status)}
                                </div>
                                <div className="space-y-1 text-sm text-gray-600">
                                  <div className="flex items-center gap-2">
                                    <MailIcon className="w-4 h-4" />
                                    <a href={`mailto:${quote.email}`} className="hover:text-pink-600">
                                      {quote.email}
                                    </a>
                                  </div>
                                  {quote.phone && (
                                    <div className="flex items-center gap-2">
                                      <PhoneIcon className="w-4 h-4" />
                                      <a href={`tel:${quote.phone}`} className="hover:text-pink-600">
                                        {quote.phone}
                                      </a>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    <span>Événement: {quote.eventType}</span>
                                  </div>
                                  {quote.eventDate && (
                                    <div className="flex items-center gap-2">
                                      <Calendar className="w-4 h-4" />
                                      <span>Date: {quote.eventDate}</span>
                                    </div>
                                  )}
                                  {quote.guestCount && (
                                    <div className="flex items-center gap-2">
                                      <User className="w-4 h-4" />
                                      <span>Invités: {quote.guestCount}</span>
                                    </div>
                                  )}
                                </div>
                                {quote.message && (
                                  <p className="mt-3 text-gray-700 bg-gray-50 p-3 rounded-lg">
                                    {quote.message}
                                  </p>
                                )}
                                <p className="text-xs text-gray-400 mt-2">
                                  Reçu le: {formatDate(quote.createdAt)}
                                </p>
                              </div>
                              <div className="flex flex-col gap-2 ml-4">
                                <Select
                                  value={quote.status}
                                  onValueChange={(value) => handleStatusChange(quote.id, value)}
                                >
                                  <SelectTrigger className="w-[150px]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="pending">En attente</SelectItem>
                                    <SelectItem value="confirmed">Confirmé</SelectItem>
                                    <SelectItem value="completed">Terminé</SelectItem>
                                    <SelectItem value="cancelled">Annulé</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button
                                  onClick={() => handleDeleteQuote(quote.id)}
                                  variant="outline"
                                  size="sm"
                                  className="border-red-500 text-red-500 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Contacts Tab */}
            <TabsContent value="contacts" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Messages de Contact</CardTitle>
                  <CardDescription>Messages reçus via le formulaire de contact</CardDescription>
                </CardHeader>
                <CardContent>
                  {contacts.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">Aucun message pour l'instant</p>
                  ) : (
                    <div className="space-y-4">
                      {contacts.map((contact) => (
                        <Card key={contact.id} className="shadow-md">
                          <CardContent className="pt-6">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h3 className="text-xl font-bold text-gray-900 mb-2">{contact.name}</h3>
                                <div className="space-y-1 text-sm text-gray-600 mb-3">
                                  <div className="flex items-center gap-2">
                                    <MailIcon className="w-4 h-4" />
                                    <a href={`mailto:${contact.email}`} className="hover:text-pink-600">
                                      {contact.email}
                                    </a>
                                  </div>
                                  {contact.phone && (
                                    <div className="flex items-center gap-2">
                                      <PhoneIcon className="w-4 h-4" />
                                      <a href={`tel:${contact.phone}`} className="hover:text-pink-600">
                                        {contact.phone}
                                      </a>
                                    </div>
                                  )}
                                </div>
                                <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                                  {contact.message}
                                </p>
                                <p className="text-xs text-gray-400 mt-2">
                                  Reçu le: {formatDate(contact.createdAt)}
                                </p>
                              </div>
                              <Button
                                onClick={() => handleDeleteContact(contact.id)}
                                variant="outline"
                                size="sm"
                                className="border-red-500 text-red-500 hover:bg-red-50 ml-4"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
